'use client';
import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import { getRequest } from '@/api/helpers';
import { routes } from '@/api/routes';
import { isEmpty } from '@/utils/common-helpers';
import BorderEdge from '../common/border-edge';
import Link from 'next/link';
import Image from 'next/image';
import TestimonialsCarouselItem from './testimonial-carousel-item';

const ClientTestimonials = () => {
    const testimonialsettings = {
        dots: true,
        arrow: true,
        autoplay: true,
        infinite: true,
        speed: 500, // Set slide transition speed
        slidesToShow: 2,
        slidesToScroll: 1,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
        ],
    };

    const [testimonials, setTestimonials] = useState([]);
    const [videoTestimonials, setVideoTestimonials] = useState([]);
    const [testimonialData, setTestimonialData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false); // State to control modal visibility
    const [currentVideo, setCurrentVideo] = useState('');

    useEffect(() => {
        fetchTestimonials().then();
        testimonial().then();
    }, []);

    const fetchTestimonials = async () => {
        const response = await getRequest(routes.HOME_TESTIMONIALS);

        if (response && response.data && response.data.length > 0) {
            const videoTestimonialData: any = [];
            const informativeTestimonials: any = [];

            response.data.map((testimonial: any) => {
                if (!isEmpty(testimonial.video_url)) {
                    videoTestimonialData.push(testimonial);
                } else {
                    informativeTestimonials.push(testimonial);
                }
            });

            setTestimonials(informativeTestimonials);
            setVideoTestimonials(videoTestimonialData);
        }
    };

    const testimonial = async () => {
        const response = await getRequest(routes.TESTIMONIALS);

        if (response && response.data.length > 0) {
            const testimonialData: any = [];

            response.data.forEach((testimonial: any) => {
                if (testimonial.image && testimonial.video_url) {
                    testimonialData.push({
                        id: testimonial.id,
                        image: testimonial.image,
                        video: testimonial.video_url,
                        project_name: testimonial.project_name,
                        client_name: testimonial.client_name,
                        description: testimonial.description,
                    });
                }
            });

            setTestimonialData(testimonialData);
        }
    };

    const playPauseVideo = (playOneVideo?: boolean, id?: string) => {
        let videos = document.querySelectorAll('video');

        if (playOneVideo) {
            videos.forEach((video) => {
                if (video.id !== id) {
                    video.pause();
                }
            });
        } else {
            videos.forEach((video) => {
                let observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.intersectionRatio !== 1 && !video.paused) {
                                video.pause();
                            }
                        });
                    },
                    { threshold: 0.2 },
                );
                observer.observe(video);
            });
        }
    };

    const handleClick = (videoSrc: any) => {
        const videoModal = document.getElementById('videoModal');
        const videoElement = videoModal?.querySelector('video');
        const cursor = document.querySelector('.cursor') as HTMLElement;
        const videoModalContent = videoModal?.querySelector('.video-modal-content') as HTMLDivElement;

        if (videoModal && videoModalContent) {
            videoModal.style.display = 'block';
            videoModal.style.opacity = '1'; // Ensure overlay is visible

            // Apply initial styles for the content
            videoModalContent.style.opacity = '0';
            videoModalContent.style.transform = ' translate(-50%, -50%) scale(0.5)';
            videoModalContent.style.transition = 'opacity 1s ease, transform 1s ease'; // Smooth transition for scaling and fading in

            // Trigger the content transition
            setTimeout(() => {
                videoModalContent.style.opacity = '1';
                videoModalContent.style.transform = ' translate(-50%, -50%) scale(1)';
            }, 10); // Short delay to ensure initial styles are applied

            document.body.style.overflow = 'hidden'; // Disable background scroll

            if (videoElement) {
                setCurrentVideo(videoSrc);
                videoElement.src = videoSrc;
                videoElement.play();
            }

            if (cursor) {
                cursor.style.width = '100px'; // Scale down cursor
                cursor.style.height = '100px'; // Scale down cursor
            }

            setIsModalOpen(true);
        }
    };

    useEffect(() => {
        setIsModalOpen(true);
    }, [handleClick]);

    const handleCloseModal = () => {
        const videoModal = document.getElementById('videoModal');
        const videoElement = videoModal?.querySelector('video');
        const cursor = document.querySelector('.cursor') as HTMLElement;
        const videoModalContent = videoModal?.querySelector('.video-modal-content') as HTMLDivElement;

        if (videoModal && videoModalContent) {
            // Apply transition for closing
            videoModalContent.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
            videoModalContent.style.opacity = '0';
            videoModalContent.style.transform = ' translate(-50%, -50%) scale(0.5)';

            // Hide the overlay and reset its opacity immediately after the transition ends
            setTimeout(() => {
                videoModal.style.opacity = '0'; // Start fading out the overlay
                setTimeout(() => {
                    videoModal.style.display = 'none'; // Hide the overlay after fade-out
                    document.body.style.overflow = ''; // Enable background scroll

                    if (videoElement) {
                        videoElement.pause();
                    }

                    if (cursor) {
                        cursor.style.width = '100px'; // Reset cursor size
                        cursor.style.height = '100px'; // Reset cursor size
                    }
                }, 500); // Matches the transition duration
            }, 0);
        }
    };
    return (
        <>
            {(testimonials.length > 0 || videoTestimonials.length > 0) && (
                <>
                    <BorderEdge color="text-green-500 dark:text-black" isTop={true} />
                    <div
                        onWheel={() => playPauseVideo()}
                        className="section section-py dark:bg-black dark:pt-0 overflow-x-hidden bg-green-500"
                    >
                        {videoTestimonials.length > 0 && (
                            <div className="container xl:container mx-auto px-4">
                                <div className="row grid lg:grid-cols-12">
                                    <div className="lg:col-span-10 gap-4 grid pb-2.5">
                                        <h2 className="text-white">See what people have to say about Moweb</h2>
                                        <p
                                            className="text-white mb-0"
                                            data-aos="fade-up"
                                            data-aos-duration="700"
                                            data-aos-delay="200"
                                        >
                                            Read client feedbacks about our services & their experience throughout the project.
                                            These customer reviews will definitely help you to make your decision smartly while
                                            choosing us over other software development companies.
                                        </p>
                                    </div>
                                    <div className="lg:col-span-12 flex flex-wrap justify-between pb-50 gap-3">
                                        <div>
                                            <p className="fs-mdlg fw-600 mb-0 text-white ">
                                                Exceeding Expectations, Delivering Excellence
                                            </p>
                                        </div>
                                        <div>
                                            <Link
                                                href="https://clutch.co/profile/moweb-technologies-private#highlights"
                                                target="_blank"
                                            >
                                                <Image
                                                    src="/img/img-clutch-testimonial.svg"
                                                    alt="clutch-frame"
                                                    title="clutch-frame"
                                                    height="24"
                                                    width="279"
                                                    loading="lazy"
                                                    className="img-fluid"
                                                />
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                {isModalOpen && (
                                    <div id="videoModal" className="video-modal">
                                        <div className="video-modal-content">
                                            <a onClick={handleCloseModal} className="close-modal">
                                                <Image
                                                    src="img/odoo/close.svg"
                                                    width={24}
                                                    height={24}
                                                    alt="Close"
                                                    className="close-modal-icon"
                                                    onClick={handleCloseModal}
                                                />
                                            </a>
                                            <video controls>
                                                <source src={currentVideo || '/media/bg-ai-ml.mp4'} type="video/mp4" />
                                            </video>
                                        </div>
                                    </div>
                                )}

                                <div className="section pb-16 dark:bg-black case-study-slider-img testimonial-slider-img">
                                    <div className="container-fluid lg:container-fluid mx-auto">
                                        <Slider
                                            {...testimonialsettings}
                                            data-aos="fade-up"
                                            data-aos-duration="2000"
                                            data-aos-delay="500"
                                        >
                                            {testimonialData.map((testimonial: any) => (
                                                <div key={testimonial.id} className="cursor-pointer">
                                                    <TestimonialsCarouselItem
                                                        path={testimonial?.image}
                                                        alt={testimonial.name}
                                                        height={400}
                                                        width={630}
                                                        data={testimonial?.description || '-'}
                                                        name={testimonial?.project_name || '-'}
                                                        post={testimonial?.client_name || '-'}
                                                        link="/"
                                                        testimonialvideo={testimonial?.video}
                                                        handleClick={handleClick}
                                                    />
                                                </div>
                                            ))}
                                        </Slider>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <BorderEdge color="text-white dark:text-black" isTop={true} bg="bg-green-500 dark:bg-black" />
                </>
            )}
        </>
    );
};
export default ClientTestimonials;
